import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PartnerType, SideDrawer, withStyles } from 'vopay';
import { useBlockingLoader } from 'shared';

import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import useBusinessTypes from '@/components/Entities/hooks/useApi/useBusinessTypes';
import useFetchPartnerBillingPackages from '@/components/Entities/hooks/useApi/useFetchPartnerBillingPackages';
import * as accountPageRoute from '@/components/Entities/Accounts/accountPage.route';
import useFetchPortalPartnerAccounts from '@/hooks/useApi/useFetchPortalPartnerAccounts';
import { toAlpha2 } from '@/constants/countries';
import DetailsStep from './DetailsStep';
import SettingsStep from './SettingsStep';
import ReviewStep from './ReviewStep';
import usePartnerAccountSubmit from './usePartnerAccountSubmit';
import useSubAccountSubmit, { SubAccountSubmitResult } from './useSubAccountSubmit';
import useWillSendOnboardingEmail from './useWillSendOnboardingEmail';
import { PARENT_ACCOUNT_FIELDS, ParentAccountOption, toParentAccountOptions } from './parentAccountOptions';
import type { BillingPackageOption, DetailsFormValues, SettingsFormValues } from './types';

import styles from './PartnerAccountCreation.module.scss';

const cn = withStyles(styles);

const STEPS = ['details', 'settings', 'review'] as const;
type Step = (typeof STEPS)[number];

/** The account an account page's Sub-Accounts tab opens the drawer under (ID-13797, spec 5.4). */
export type LockedParentAccount = {
  accountID: string;
  accountName: string;
  /** Alpha-2, from the account the tab lists: decides the US business-phone requirement before the book lands. */
  country: string;
  currency: string;
};

/** The partner account the partner page's Accounts tab opens the drawer for (ID-13795). */
export type ScopedPartnerAccount = {
  /**
   * Its own JWT, as `/partner/partners` returns it with `ShowToken`. The create, business-case and
   * billing-package routes act as whichever partner signs them, so the account is created under —
   * and by the rules of — this partner account.
   */
  bearer: string;
  name: string;
  /** Decides whether the onboarding email will send. */
  partnerType: PartnerType;
};

// Both hosts mount the drawer only while it is open (the route host by route, the Accounts page by
// state), so there is no isOpen prop — mounting is what opens it.
type PartnerAccountCreationProps = {
  // Called whenever the drawer should close — the route host navigates back to /accounts, the
  // Accounts-page host just unmounts the drawer.
  onClose: () => void;
  // Called after a successful top-level creation, before onClose — the Accounts-page host refreshes
  // its list. A sub-account creation navigates to the new account instead and does not call it.
  onCreated?: () => void;
  /**
   * Set when opened from an account's Sub-Accounts tab: the form starts in sub-account mode under
   * this account, and the parent cannot be changed. Absent, the Parent Account picker decides.
   */
  parentAccount?: LockedParentAccount;
  /** Set when opened from a partner account's page: the account is created under that partner account. */
  partnerAccount?: ScopedPartnerAccount;
};

export default function PartnerAccountCreation({
  onClose,
  onCreated,
  parentAccount: lockedParent,
  partnerAccount,
}: PartnerAccountCreationProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'PartnerAccountCreation' });
  const history = useHistory();
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const blockingLoader = useBlockingLoader();
  const { billingPackages, fetchBillingPackages } = useFetchPartnerBillingPackages({ bearer: partnerAccount?.bearer });
  const { businessTypes, fetchBusinessTypes } = useBusinessTypes();
  const { submitPartnerAccount } = usePartnerAccountSubmit({ bearer: partnerAccount?.bearer });
  const { submitSubAccount } = useSubAccountSubmit();
  const willSendOnboardingEmail = useWillSendOnboardingEmail(partnerAccount?.partnerType);

  const {
    response: { isRoleAdmin, isInsideSales, accountId: ownPartnerAccountID },
  } = usePartnerData();

  // The partner's whole book, for the Parent Account picker (spec 5.1). Loaded on every open so an
  // account whose sub-accounts were just enabled is offered the next time.
  const {
    accounts: bookAccounts,
    fetching: fetchingBookAccounts,
    fetchPortalPartnerAccounts,
  } = useFetchPortalPartnerAccounts();

  // Under a partner account the picker would list the session's book, not that partner account's,
  // so the drawer creates top-level accounts only there.
  const canCreateSubAccounts = isRoleAdmin && !isInsideSales && !partnerAccount;

  const [step, setStep] = useState<Step>('details');
  const [details, setDetails] = useState<DetailsFormValues | null>(null);
  const [settings, setSettings] = useState<SettingsFormValues | null>(null);
  const [submitted, setSubmitted] = useState(false);
  // A refused sub-account creation, shown inline on Review (spec 5.5).
  const [submitError, setSubmitError] = useState('');
  // Set when the user confirms discarding a draft. A ref, not state: onClose() navigates in the
  // same tick, so the navigation guard runs before a state update could re-render — the ref is read
  // synchronously inside history.block below.
  const isDiscardingDraftRef = useRef(false);

  useEffect(() => {
    // Under a locked parent the industry dropdown never shows, so its list is not needed.
    if (lockedParent) return;

    // The industry dropdown renders its options from this, and Industry is required — a failure
    // leaves the step unfillable, so it must be surfaced (the hook already logged it).
    fetchBusinessTypes().catch((error) => {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('Error.businessTypesFailed', {
          defaultValue: 'Could not load the industry list: {{errorMessage}}',
          errorMessage,
        }),
      );
    });
  }, []);

  useEffect(() => {
    // Only an Admin may create sub-accounts (spec 5.1), and not an Inside Sales partner (product,
    // 2026-10-09); for anyone else the drawer is the top-level form as before, and the book is
    // never asked for.
    if (!canCreateSubAccounts) return;

    fetchPortalPartnerAccounts({ Fields: PARENT_ACCOUNT_FIELDS }).catch((error) => {
      // The hook has logged the cause. Without the list there is no parent to offer, so the drawer
      // stays a top-level form — or, under a locked parent, keeps the one it was given.
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('Error.parentAccountsFailed', {
          defaultValue: 'Could not load the parent accounts: {{errorMessage}}',
          errorMessage,
        }),
      );
    });
  }, []);

  const lockedParentID = lockedParent?.accountID;
  const lockedParentName = lockedParent?.accountName;
  const lockedParentCountry = lockedParent?.country;
  const lockedParentCurrency = lockedParent?.currency;

  const parentAccountOptions = useMemo(() => {
    const options = toParentAccountOptions(bookAccounts, ownPartnerAccountID);

    // The locked parent has to be an option before the book lands (and if it never does), or the
    // read-only picker would have nothing to show for it. The tab's own account row supplies the
    // country and currency, so a US parent asks for the phone from the first render; only the
    // billing package has to wait for the book.
    if (lockedParentID && lockedParentName && !options.some((option) => option.accountID === lockedParentID)) {
      options.unshift({
        accountID: lockedParentID,
        accountName: lockedParentName,
        path: [lockedParentName],
        partnerAccountName: '',
        country: lockedParentCountry ?? '',
        currency: lockedParentCurrency ?? '',
        billingPackageName: '',
      });
    }

    return options;
  }, [bookAccounts, ownPartnerAccountID, lockedParentID, lockedParentName, lockedParentCountry, lockedParentCurrency]);

  const isParentFieldShown = !!lockedParent || (canCreateSubAccounts && parentAccountOptions.length > 0);
  // The card holds its place with a skeleton while the book loads, rather than appearing a beat
  // after the rest of the step; a locked parent is already an option, so it never waits.
  const isLoadingParentAccounts = !lockedParent && canCreateSubAccounts && fetchingBookAccounts;

  const selectedParent = useMemo<ParentAccountOption | null>(
    () =>
      details?.parentAccountID
        ? (parentAccountOptions.find((option) => option.accountID === details.parentAccountID) ?? null)
        : null,
    [details?.parentAccountID, parentAccountOptions],
  );

  useEffect(() => {
    if (!details?.country) return;

    // The endpoint filters on ADRCountries.Abbreviation (alpha-2). The form stores alpha3, which
    // matches no row and comes back as an empty package list instead of an error.
    const alpha2 = toAlpha2(details.country);

    if (!alpha2) return;

    fetchBillingPackages(alpha2).catch((error) => {
      // The hook has logged the cause. Without the list the required package cannot be picked.
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('Error.billingPackagesFailed', {
          defaultValue: 'Could not load the billing packages: {{errorMessage}}',
          errorMessage,
        }),
      );
    });
  }, [details?.country]);

  const billingPackageOptions = useMemo<BillingPackageOption[]>(
    () =>
      billingPackages.map((billingPackage) => ({ value: billingPackage.PackageID, label: billingPackage.PackageName })),
    [billingPackages],
  );

  const displaySteps = [
    t('Steps.details', { defaultValue: 'Details' }),
    t('Steps.settings', { defaultValue: 'Settings' }),
    t('Steps.review', { defaultValue: 'Review' }),
  ];

  const currentStepIdx = STEPS.indexOf(step);

  const goToStep = (nextStep: Step) => {
    // Leaving Review to edit clears a refusal: the retry gets a fresh verdict.
    setSubmitError('');
    setStep(nextStep);
  };

  const nextStep = () => {
    if (currentStepIdx < STEPS.length - 1) goToStep(STEPS[currentStepIdx + 1]);
  };

  const prevStep = () => {
    if (currentStepIdx > 0) goToStep(STEPS[currentStepIdx - 1]);
  };

  const hasNonEmptyValue = (value: unknown): boolean => {
    if (value === '' || value === null || value === false || typeof value === 'undefined') return false;
    if (typeof value === 'object') return Object.values(value).some(hasNonEmptyValue);

    return true;
  };

  const hasPendingData = useMemo(() => {
    // A parent the tab pre-filled is not something the user typed.
    const typedDetails = details && { ...details, parentAccountID: lockedParent ? '' : details.parentAccountID };

    return [typedDetails, settings].some((values) => !!values && Object.values(values).some(hasNonEmptyValue));
  }, [details, settings, lockedParent]);

  const unsavedChangesMessage = t('Prompt.unsavedChanges', {
    defaultValue: 'Your information will not be saved if you leave now. Are you sure you want to continue?',
  });

  async function confirmDiscardDraft() {
    if (!hasPendingData || submitted) return true;

    const choice = await alertDialog.caution(
      t('Prompt.title', { defaultValue: 'Discard this draft?' }),
      unsavedChangesMessage,
      {
        confirmLabel: t('Prompt.discard', { defaultValue: 'Discard' }),
        abortLabel: t('Prompt.keepEditing', { defaultValue: 'Keep Editing' }),
      },
    );

    return choice === 'confirm';
  }

  const handleClose = async () => {
    if (!(await confirmDiscardDraft())) return;

    isDiscardingDraftRef.current = true;
    onClose();
  };

  // react-router's <Prompt> routes its confirmation through the app's getUserConfirmation, which
  // renders a plain modal into <body> — beneath this Radix drawer, where it is invisible and
  // unclickable. So block the transition here and ask with the dialog the rest of the drawer uses.
  useEffect(() => {
    if (!hasPendingData || submitted) return undefined;

    const unblock = history.block((location) => {
      // A close the user already confirmed navigates on its own; don't ask twice.
      if (isDiscardingDraftRef.current) return undefined;

      confirmDiscardDraft().then((isDiscarding) => {
        if (!isDiscarding) return;

        isDiscardingDraftRef.current = true;
        unblock();

        // Pushed, browser moves included: a back and a forward both arrive as 'POP', so replaying one
        // with goBack() sent a forward the wrong way.
        history.push(`${location.pathname}${location.search}`);
      });

      return false;
    });

    return unblock;
  }, [hasPendingData, submitted, history]);

  // Sub-account mode (spec 5.5): one call, no follow-ups. A refusal stays on Review with the
  // backend's message; a success goes straight to the new sub-account's page.
  const handleSubAccountSubmit = async (draft: DetailsFormValues, parent: ParentAccountOption) => {
    blockingLoader.show();
    let result: SubAccountSubmitResult | null;

    try {
      result = await submitSubAccount(draft, parent);
    } catch {
      // The hook has already logged the failure.
      result = null;
    } finally {
      blockingLoader.hide();
    }

    if (!result?.success) {
      setSubmitError(
        result?.errorMessage ||
          t('Error.creationFailedMessage', { defaultValue: 'An error occurred. Please try again.' }),
      );

      return;
    }

    setSubmitted(true);

    toast.success(
      t('Success.subAccountCreated', {
        defaultValue: 'Sub-account {{name}} created under {{parentName}}.',
        // The name the backend gives the account: the DBA when given, else the legal name.
        name: draft.dbaName.trim() || draft.legalBusinessName.trim(),
        parentName: parent.accountName,
      }),
    );

    // No onCreated here: the drawer leaves for the new sub-account's page, whose own fetch is what
    // the user sees next, so a list refresh fired now would only race that navigation.
    // The submission is done: nothing left to guard on the way to the new page.
    isDiscardingDraftRef.current = true;
    history.push(accountPageRoute.getPath({ accountID: result.accountID }));
  };

  const handleSubmit = async () => {
    if (!details || !settings) return;

    setSubmitError('');

    if (selectedParent) {
      await handleSubAccountSubmit(details, selectedParent);

      return;
    }

    blockingLoader.show();
    let result;

    try {
      result = await submitPartnerAccount(details, settings);
    } catch {
      // The hook has already logged the failure.
      result = null;
    } finally {
      blockingLoader.hide();
    }

    if (!result || !result.success) {
      alertDialog.error(
        t('Error.creationFailedTitle', { defaultValue: 'Account Creation Failed' }),
        result?.errorMessage ||
          t('Error.creationFailedMessage', { defaultValue: 'An error occurred. Please try again.' }),
      );

      return;
    }

    setSubmitted(true);

    // Account was created, but one or more post-creation sub-requests failed. R1's
    // AccountCreatedWithErrorsDialog opens through the plain Modal, which layers under the Radix
    // SideDrawer — inside the drawer the Radix AlertDialog is the one that stacks correctly.
    if (result.issues.length > 0) {
      const choice = await alertDialog.caution(
        t('CreatedWithErrors.title', { defaultValue: 'Account Created — Errors Need Your Attention' }),
        <div className={cn('issues')}>
          <p className={cn('issuesDescription')}>
            {t('CreatedWithErrors.description', {
              defaultValue: "Your account has been created, but we found a few issues you'll need to address.",
            })}
          </p>

          <ul className={cn('issuesList')}>
            {result.issues.map((issue, index) => (
              <li key={`${index}-${issue}`}>{issue}</li>
            ))}
          </ul>
        </div>,
        {
          confirmLabel: t('CreatedWithErrors.goToAccount', { defaultValue: 'Go to Account' }),
          abortLabel: t('CreatedWithErrors.fixLater', { defaultValue: 'Fix Later' }),
        },
      );

      onCreated?.();

      if (choice === 'confirm') {
        history.push(accountPageRoute.getPath({ accountID: result.accountID, currency: result.accountCurrency }));
      } else {
        // "Fix Later" (or dismiss) → back to the accounts list
        onClose();
      }

      return;
    }

    await alertDialog.success(
      t('Success.createdTitle', { defaultValue: 'Success! Account Created' }),
      t('Success.createdMessage', { defaultValue: 'Your account has been created' }),
      { confirmLabel: t('Success.goToAccounts', { defaultValue: 'Go to Accounts' }) },
    );

    onCreated?.();
    onClose();
  };

  return (
    <SideDrawer
      isOpen
      onClose={handleClose}
      title={
        lockedParent
          ? t('subAccountTitle', { defaultValue: 'Add Sub-Account' })
          : t('title', { defaultValue: 'Add VoPay Account' })
      }
      subtitle={
        partnerAccount &&
        t('partnerAccountSubtitle', {
          defaultValue: 'For the partner account {{partnerName}}',
          partnerName: partnerAccount.name,
        })
      }
      style={{ width: '100%', maxWidth: '100%' }}
    >
      <div className={cn('container')}>
        <div className={cn(step !== 'details' && 'hidden', 'stepContainer')} data-testid="accountCreationStep-details">
          <DetailsStep
            steps={displaySteps}
            currentStep={currentStepIdx}
            businessTypes={businessTypes}
            parentAccountOptions={parentAccountOptions}
            isParentFieldShown={isParentFieldShown}
            isLoadingParentAccounts={isLoadingParentAccounts}
            lockedParentAccountID={lockedParentID}
            willSendOnboardingEmail={willSendOnboardingEmail}
            nextStep={nextStep}
            onValuesChange={setDetails}
          />
        </div>

        <div
          className={cn(step !== 'settings' && 'hidden', 'stepContainer')}
          data-testid="accountCreationStep-settings"
        >
          <SettingsStep
            steps={displaySteps}
            currentStep={currentStepIdx}
            country={details?.country ?? ''}
            parentAccount={selectedParent}
            billingPackageOptions={billingPackageOptions}
            nextStep={nextStep}
            prevStep={prevStep}
            onValuesChange={setSettings}
          />
        </div>

        <div className={cn(step !== 'review' && 'hidden', 'stepContainer')} data-testid="accountCreationStep-review">
          <ReviewStep
            steps={displaySteps}
            currentStep={currentStepIdx}
            details={details}
            settings={settings}
            businessTypes={businessTypes}
            parentAccount={selectedParent}
            submitError={submitError}
            billingPackageOptions={billingPackageOptions}
            willSendOnboardingEmail={willSendOnboardingEmail}
            goToStep={goToStep}
            onSubmit={handleSubmit}
          />
        </div>
      </div>
    </SideDrawer>
  );
}
